<template>
  <div class="py-1">
    <p class="mb-2">
      <span>{{ $t('components.workflow.action_delay.label') }} </span>
      <span v-if="isDelayed" class="text-error">*</span>
    </p>

    <div class="flex items-center gap-2">
      <button
        type="button"
        :disabled="disabled"
        class="flex items-center mb-6 text-sm"
        @click="setIsDelayed"
      >
        <span
          class="p-2 rounded-l-lg border"
          :class="{
            'bg-main text-white-text border-main': !isDelayed,
            'bg-primary-200 text-base-content-200 border-primary-200': !isDelayed && disabled
          }"
        >
          {{ $t('components.workflow.action_delay.now') }}
        </span>
        <span
          class="p-2 rounded-r-lg border"
          :class="{
            'bg-main text-white-text border-main': isDelayed,
            'bg-primary-200 text-base-content-200 border-primary-200': isDelayed && disabled
          }"
        >
          {{ $t('components.workflow.action_delay.later') }}
        </span>
      </button>

      <div class="flex items-center mb-6">
        <FormulateInput
          v-model="delayValue"
          type="number"
          :min="0"
          :max="selectedLimit.value"
          class="flex-1 flex flex-col w-20 action-delay-number"
          :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
          :disabled="!isDelayed || disabled"
          @input="validateInput"
          @keypress="sanitizeNumberInput"
          @blur="removeLeadingZeros"
        />

        <Dropdown
          v-model="unit"
          :options="units"
          v-bind="dropdownConfig"
          width="120px"
          class="w-44 h-5 border rounded-md action-delay-unit"
          :class="{ 'bg-white': !disabled, 'text-base-content-300': disabled }"
        >
          <template #before>
            <p class="mr-4 font-normal">{{ displayedName }}</p>
          </template>
          <template #caret>
            <font-awesome-icon icon="angle-down" />
          </template>
        </Dropdown>
      </div>
    </div>

    <p v-if="isErrorTextShown" class="text-sm text-error">{{ selectedLimit.text }}</p>
  </div>
</template>

<script>
import Dropdown from "@shared/components/dropdown-base";

export default {
  name: "ActionDelay",

  components: {
    Dropdown,
  },

  props: {
    model: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isDelayed() {
      return this.model.isDelayed;
    },

    delayValue: {
      get() {
        return this.model.value;
      },
      set(value) {
        this.$emit("select", { key: "value", value });
      }
    },

    unit: {
      get() {
        return this.model.unit;
      },
      set(value) {
        this.$emit("select", { key: "unit", value: value.id });
      }
    },

    isInvalid() {
      return this.model.isInValid;
    },

    displayedName() {
      return this.item.name;
    },

    item() {
      return this.units.find(el => el.id === this.unit) ?? {};
    },

    units() {
      return [{
        id: "MINUTE",
        name: this.$t('components.workflow.action_delay.minutes'),
      }, {
        id: "HOUR",
        name: this.$t('components.workflow.action_delay.hours'),
      }, {
        id: "DAY",
        name: this.$t('components.workflow.action_delay.days'),
      }];
    },

    isErrorTextShown() {
      return Number(this.delayValue) > this.selectedLimit.value;
    },

    selectedLimit() {
      return this.limits[this.unit] ?? {};
    },

    limits() {
      return {
        "MINUTE": {
          value: 5256000,
          text: this.$t('components.workflow.action_delay.validations.minutes'),
        },
        "HOUR": {
          value: 86700,
          text: this.$t('components.workflow.action_delay.validations.hours'),
        },
        "DAY": {
          value: 3650,
          text: this.$t('components.workflow.action_delay.validations.days'),
        }
      };
    },

    dropdownConfig() {
      return {
        config: { trackBy: "id", label: "name" },
        disabled: !this.isDelayed || this.disabled,
      };
    },
  },

  methods: {
    sanitizeNumberInput(event) {
      const { keyCode } = event.target;
      if (keyCode < 48 ||keyCode > 57) {
        event.preventDefault();
      }
    },

    validateInput(value) {
      this.$emit("select", {
        key: "isInValid",
        value: Number(value) > this.selectedLimit.value || !value.length || Number(value) === 0,
      });
    },

    removeLeadingZeros(event) {
      const { value } = event.target;
      let updatedValue = null;
      updatedValue = value.length > 1 ? value.replace(/^0+/, "") : value;

      this.$emit("select", { key: "value", value: updatedValue });
    },

    setIsDelayed() {
      const value = !this.isDelayed;
      this.$emit("select", { key: "isDelayed", value });
      this.$emit("select", { key: "isInValid", value });
      this.$emit("select", { key: "value", value: "" });
      this.$emit("select", { key: "unit", value: value ? "DAY" : "" });
    },
  },
}
</script>

<style lang="scss">
.action-delay-number.formulate-input {
  .formulate-input-element > input,
  .formulate-input-element > textarea {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.action-delay-unit {
  margin-bottom: 1px;
  margin-left: -1px;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
}
</style>
